import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export const exportMonthlyReportPDF = (
  monthlyReportData,
  year,
  isFullYear = true
) => {
  try {
    const doc = new jsPDF();

    const months = [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ];

    // Title
    doc.setFontSize(16);
    const title = isFullYear
      ? `Yearly Report - ${year}`
      : `Monthly Report - ${
          monthlyReportData[0]?.month
            ? months.find((m) => m.value === monthlyReportData[0].month)
                ?.label || `Month ${monthlyReportData[0].month}`
            : "Unknown Month"
        } ${year}`;
    doc.text(title, 14, 20);

    // Prepare table data
    const tableData =
      monthlyReportData.length > 0
        ? monthlyReportData.map((report) => [
            months.find((m) => m.value === report.month)?.label ||
              `Month ${report.month}`,
            report.total_boxes,
            report.active_boxes,
            report.disconnected_boxes,
            ` ${parseFloat(report.total_collection).toFixed(2)}`,
          ])
        : [["No Data", 0, 0, 0, "0.00"]]; // Fallback for empty data

    // Generate table
    autoTable(doc, {
      head: [
        [
          "Month",
          "Total Boxes",
          "Active Boxes",
          "Disconnected Boxes",
          "Total Collection",
        ],
      ],
      body: tableData,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString("en-IN", {
          timeZone: "Asia/Kolkata",
        })} | Page ${i} of ${pageCount}`,
        14,
        doc.internal.pageSize.height - 10
      );
    }

    // Save file
    const fileName = isFullYear
      ? `Yearly_Report_${year}.pdf`
      : `Monthly_Report_${
          monthlyReportData[0]?.month
            ? months
                .find((m) => m.value === monthlyReportData[0].month)
                ?.label.replace(" ", "_") ||
              `Month_${monthlyReportData[0].month}`
            : "Unknown_Month"
        }_${year}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("PDF Export Error:", error);
    throw error;
  }
};

export const exportMonthlyReportExcel = (
  monthlyReportData,
  year,
  isFullYear = true
) => {
  try {
    const months = [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ];

    // Prepare data
    const excelData =
      monthlyReportData.length > 0
        ? monthlyReportData.map((report) => ({
            Month:
              months.find((m) => m.value === report.month)?.label ||
              `Month ${report.month}`,
            "Total Boxes": report.total_boxes,
            "Active Boxes": report.active_boxes,
            "Disconnected Boxes": report.disconnected_boxes,
            "Total Collection": ` ${parseFloat(report.total_collection).toFixed(
              2
            )}`,
          }))
        : [
            {
              Month: "No Data",
              "Total Boxes": 0,
              "Active Boxes": 0,
              "Disconnected Boxes": 0,
              "Total Collection": "0.00",
            },
          ]; // Fallback for empty data

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Add headers styling
    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const cell_address = { c: C, r: 0 };
      const cell_ref = XLSX.utils.encode_cell(cell_address);
      if (!ws[cell_ref]) continue;
      ws[cell_ref].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "FF2980B9" } },
        alignment: { horizontal: "center" },
      };
    }

    // Auto-fit columns
    const colWidths = [
      { wch: 15 }, // Month
      { wch: 12 }, // Total Boxes
      { wch: 12 }, // Active Boxes
      { wch: 20 }, // Disconnected Boxes
      { wch: 18 }, // Total Collection
    ];
    ws["!cols"] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Monthly Report");

    // Add summary sheet for full year
    if (isFullYear && monthlyReportData.length > 0) {
      const summaryData = [
        {
          Year: year,
          "Total Months": monthlyReportData.length,
          "Grand Total Boxes": monthlyReportData.reduce(
            (sum, r) => sum + r.total_boxes,
            0
          ),
          "Grand Active Boxes": monthlyReportData.reduce(
            (sum, r) => sum + r.active_boxes,
            0
          ),
          "Grand Disconnected Boxes": monthlyReportData.reduce(
            (sum, r) => sum + r.disconnected_boxes,
            0
          ),
          "Grand Total Collection": `${parseFloat(
            monthlyReportData.reduce(
              (sum, r) => sum + parseFloat(r.total_collection),
              0
            )
          ).toFixed(2)}`,
        },
      ];
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
    }

    // Generate filename
    const fileName = isFullYear
      ? `Yearly_Report_${year}.xlsx`
      : `Monthly_Report_${
          monthlyReportData[0]?.month
            ? months
                .find((m) => m.value === monthlyReportData[0].month)
                ?.label.replace(" ", "_") ||
              `Month_${monthlyReportData[0].month}`
            : "Unknown_Month"
        }_${year}.xlsx`;

    // Save Excel file
    XLSX.writeFile(wb, fileName);
  } catch (error) {
    console.error("Excel Export Error:", error);
    throw error;
  }
};

export const getMonthLabel = (monthNumber) => {
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];
  return (
    months.find((m) => m.value === monthNumber)?.label || `Month ${monthNumber}`
  );
};
